import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/common/components/ui/button";
import { motion, AnimatePresence } from "framer-motion";
import ProductLayout from "@/projects/IPO/components/ProductLayout";
import CompanyHeader from "./components/CompanyHeader";
import TabNavigation from "./components/TabNavigation";
import OverviewTab from "./tabs/OverviewTab";
import FinancialsTab from "./tabs/FinancialsTab";
import ValuationTab from "./tabs/ValuationTab";
import OwnershipTab from "./tabs/OwnershipTab";
import TechnicalsTab from "./tabs/TechnicalsTab";
import EventsTab from "./tabs/EventsTab";
import NewsTab from "./tabs/NewsTab";
import { Building2 } from "lucide-react";
import { Loader } from "@/common/components/ui";
import ScreenerService from "@/services/Screener/APIs/ScreenerService";
import { Company, NSEDataItem } from "@/utils/types/Screener/Screener";

// Map API Company type to component format
const mapCompanyToComponentFormat = (company: Company): any => ({
  // Include all Company fields first
  ...company,
  // Override with component-specific format
  id: company.id ?? company.co_code?.toString() ?? "",
  name: company.companyname ?? "",
  shortName: company.companyshortname ?? "",
  bselisted: company.bselisted ?? "no",
  nselisted: company.nselisted ?? "no",
  mcaptype: company.mcaptype ?? "N/A",
  mcap: company.mcap ?? 0,
  sectorcode: company.sectorcode ?? "N/A",
  industrycode: company.industrycode ?? "N/A",
  sectorname: company.sectorname ?? "N/A",
  industryname: company.industryname ?? "N/A",
  bsegroup: company.bsegroup ?? "N/A",
  bsecode: company.bsecode ?? "",
  nsesymbol: company.nsesymbol ?? "",
  displaytype: company.displaytype ?? "N/A",
  bse_code: company.bsecode ?? "",
  sector: company.sectorcode ?? "N/A",
  incorporationDate: company.inc_dt ?? "N/A",
  website: company.internet ?? "N/A",
  faceValue: company.fv ?? "N/A",
  industry: company.industryname ?? company.sectorname ?? "N/A",
  isin: company.isin ?? null,
  price: 0, // Not available in API response
  marketCap: company.mcap ?? 0,
  change: 0, // Not available in API response
  peRatio: 0, // Not available in API response
  weekHigh52: 0, // Not available in API response
  weekLow52: 0, // Not available in API response
  volume: 0, // Not available in API response
  description: `${company.companyname ?? ""} is a leading enterprise in the ${company.sectorname ?? ""} industry.`,
});

const CompanyDetailsPage = () => {
  const { id, symbol } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get("tab") ?? "overview";
  const [company, setCompany] = useState<any>(null);
  const fetchingRef = useRef<string | null>(null);
  const [nseData, setNseData] = useState<NSEDataItem[] | null>(null);
  const [nseLoading, setNseLoading] = useState(false);
  const [nseError, setNseError] = useState<string | null>(null);
  const [isTabLoading, setIsTabLoading] = useState(false);
  const accumulatedDataRef = useRef<NSEDataItem[]>([]);
  const nseFetchingRef = useRef<string | null>(null);
  const nseOpenPriceRef = useRef<number | null>(null);
  const nsePreviousCloseRef = useRef<number | null>(null);
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [latestPivot, setLatestPivot] = useState<any>(null);

  // Fetch company data
  useEffect(() => {
    // Early return if no ID or symbol
    if (!id && !symbol) {
      setCompany(null);
      fetchingRef.current = null;
      return;
    }

    // Create a unique key for this request
    const requestKey = id ?? symbol ?? "";

    // Prevent duplicate calls for the same id/symbol
    // This handles React StrictMode double-invocation
    // Set synchronously to prevent race conditions
    if (fetchingRef.current === requestKey) {
      return;
    }

    // Mark that we're fetching this id/symbol (set synchronously before async call)
    fetchingRef.current = requestKey;

    const fetchCompany = async () => {
      try {
        // Use getCompanyMasterById when ID is available
        if (id) {
          const response = await ScreenerService.getCompanyMasterById(id);

          // Only update state if this is still the current request
          // This prevents race conditions and duplicate updates
          if (fetchingRef.current === requestKey) {
            if (response?.success && response?.data) {
              setCompany(mapCompanyToComponentFormat(response.data));
            } else {
              setCompany(null);
            }
            fetchingRef.current = null;
          }
        } else if (symbol) {
          // If only symbol is provided, fetch paginated list and search
          const response = await ScreenerService.getCompanyMaster({
            page: 1,
            page_size: 100,
          });

          // Only update state if this is still the current request
          if (fetchingRef.current === requestKey) {
            if (response?.success && response?.data) {
              const found = response.data.results.find(
                item =>
                  item.nsesymbol?.toLowerCase() === symbol.toLowerCase() ||
                  item.bsecode?.toLowerCase() === symbol.toLowerCase()
              );

              if (found) {
                // Convert ScreenerItem to Company format (if needed)
                setCompany(mapCompanyToComponentFormat(found as any));
              } else {
                setCompany(null);
              }
            } else {
              setCompany(null);
            }
            fetchingRef.current = null;
          }
        }
      } catch (error) {
        // Only update state if this is still the current request
        if (fetchingRef.current === requestKey) {
          console.error("Error fetching company:", error);
          setCompany(null);
          fetchingRef.current = null;
        }
      }
    };

    fetchCompany();

    // Cleanup function to mark that this request is no longer active
    return () => {
      // Only clear if this was the current request
      // Removed the reset here to prevent StrictMode from re-triggering immediately
      // if (fetchingRef.current === requestKey) {
      //   fetchingRef.current = null;
      // }
    };
  }, [id, symbol]);

  useEffect(() => {
    const fetchPivotData = async () => {
      if (company?.co_code) {
        try {
          const response = await ScreenerService.getPivotFibonacciData(
            company.co_code.toString(),
            "day",
            "NSE"
          );
          if (response.success && response.data && response.data.length > 0) {
            // Sort by CurrTime descending to get the latest
            const sortedData = [...response.data].sort((a, b) => {
              return (
                new Date(b.CurrTime).getTime() - new Date(a.CurrTime).getTime()
              );
            });
            setCurrentPrice(sortedData[0].currprice);
            setLatestPivot(sortedData[0]);
          }
        } catch (error) {
          console.error("Failed to fetch pivot data:", error);
        }
      }
    };

    fetchPivotData();
  }, [company?.co_code]);

  // Fetch NSE data when company is loaded
  useEffect(() => {
    if (!company?.co_code && !company?.id) {
      setNseData(null);
      accumulatedDataRef.current = [];
      return;
    }

    const coCode = company.co_code ?? company.id;
    if (!coCode) {
      return;
    }

    // Reset accumulated data when company changes
    console.log("Resetting accumulated data due to company change");
    accumulatedDataRef.current = [];
    nseOpenPriceRef.current = null;
    nsePreviousCloseRef.current = null;
    // Reset NSE fetching ref so new company can fetch
    nseFetchingRef.current = null;

    const fetchNSEData = async () => {
      const currentCoCode = coCode.toString();

      // Prevent duplicate NSE calls for the same company
      if (nseFetchingRef.current === currentCoCode) {
        return;
      }
      nseFetchingRef.current = currentCoCode;

      try {
        setNseLoading(true);
        setNseError(null);
        const response = await ScreenerService.getNSEData(coCode.toString());

        if (response?.success && response?.data && response.data.length > 0) {
          const latestData = response.data[0]; // Get the latest data point
          const now = new Date();

          // Store opening price from first API call
          if (nseOpenPriceRef.current === null && latestData.open_price) {
            nseOpenPriceRef.current = parseFloat(latestData.open_price);
          }

          // Store previous close (yesterday's close) from first API call
          // Calculate from current price and change, or use opening price as proxy
          if (nsePreviousCloseRef.current === null) {
            // If we have opening price, use it as yesterday's close proxy
            // Otherwise calculate: Previous Close = Current Price / (1 + Change%)
            if (latestData.open_price) {
              nsePreviousCloseRef.current = parseFloat(latestData.open_price);
            } else if (latestData.price) {
              // Fallback: use opening price if available, otherwise estimate
              nsePreviousCloseRef.current = parseFloat(latestData.price);
            }
          }

          // Always add a new data point with current timestamp for live updates
          // This ensures we get a continuous line chart with time progression
          const newDataPoint: NSEDataItem = {
            ...latestData,
            tr_date: now.toISOString(), // Use current time for live updates
          };

          // Add to accumulated data
          accumulatedDataRef.current = [
            ...accumulatedDataRef.current,
            newDataPoint,
          ].slice(-50); // Keep last 50 data points

          setNseData([...accumulatedDataRef.current]);
        } else {
          // Only set error if we don't have any existing data
          if (accumulatedDataRef.current.length === 0) {
            setNseError("No data available");
            setNseData(null);
          }
        }
      } catch (err) {
        console.error("Error fetching NSE data:", err);
        // Only set error if we don't have any existing data
        if (accumulatedDataRef.current.length === 0) {
          setNseError("Failed to fetch data");
          setNseData(null);
        }
      } finally {
        setNseLoading(false);
      }
    };

    // Initial fetch
    fetchNSEData();

    return () => {
      accumulatedDataRef.current = [];
    };
  }, [company?.co_code, company?.id]);

  const handleTabChange = (tab: string) => {
    setSearchParams({ tab });
  };

  // if (loading) {
  //   return (
  //     <ProductLayout>
  //       <Loader />
  //     </ProductLayout>
  //   );
  // }

  if (!company) {
    return (
      <ProductLayout>
        <div className="container mx-auto px-4 py-32 text-center text-slate-500">
          <div className="mx-auto max-w-md rounded-lg border border-slate-100 bg-white p-8 shadow-sm">
            <Building2 className="mx-auto mb-4 h-12 w-12 text-slate-300" />
            <h2 className="mb-2 font-sans text-2xl font-bold text-slate-800">
              Company Unavailable
            </h2>
            <p className="mb-6 font-sans text-base">
              The requested records could not be retrieved from the terminal.
            </p>
            <Button
              className="w-full rounded-md font-sans"
              onClick={() => navigate("/screener")}
            >
              Return to Screener
            </Button>
          </div>
        </div>
      </ProductLayout>
    );
  }

  return (
    <ProductLayout>
      <div className="flex min-h-screen flex-col bg-[#f8fafc]">
        {isTabLoading && <Loader />}
        <CompanyHeader
          company={company}
          nsePrice={
            nseData && nseData.length > 0
              ? parseFloat(nseData[nseData.length - 1].price)
              : null
          }
          currentPrice={currentPrice}
          nsePreviousClose={nsePreviousCloseRef.current}
          nseLoading={nseLoading}
        />
        <TabNavigation activeTab={activeTab} onTabChange={handleTabChange} />

        <main className="container mx-auto flex-1 px-4 py-8 md:px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeTab}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {activeTab === "overview" && (
                <OverviewTab
                  company={company}
                  nseData={nseData}
                  nseLoading={nseLoading}
                  nseError={nseError}
                />
              )}
              {activeTab === "financials" && (
                <FinancialsTab
                  company={company}
                  setIsLoading={setIsTabLoading}
                />
              )}
              {activeTab === "valuation" && <ValuationTab company={company} />}
              {activeTab === "ownership" && <OwnershipTab company={company} />}
              {activeTab === "technicals" && (
                <TechnicalsTab company={company} latestPivot={latestPivot} />
              )}
              {activeTab === "events" && <EventsTab company={company} />}
              {activeTab === "news" && <NewsTab company={company} />}
            </motion.div>
          </AnimatePresence>
        </main>
      </div>
    </ProductLayout>
  );
};

export default CompanyDetailsPage;
